import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { formatRelativeDate as formatRelativeDateUtil } from '../utils/relativeDate';
import { useNavigate } from 'react-router-dom';
import { workflowService, dashboardService, tagService, WorkflowFilters } from '../services';
import { Workflow, Dashboard, Tag } from '../types';
import { useThrottle } from '../hooks';
import { getErrorMessage } from '../utils/errors';
import ImportModal from '../components/common/ImportModal';
import TagBadge from '../components/common/TagBadge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import DescriptionCells from '../components/common/ExpandableDescription';
import Select from '../components/ui/Select';
import LibraryListPage from '../components/ui/LibraryListPage';
import SortHeader from '../components/ui/SortHeader';

const StepsIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const STATUS_VARIANTS: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

const WIDGET_TYPE_OPTIONS = [
  { value: 'qa', label: 'Q&A' },
  { value: 'conviction', label: 'Conviction Score' },
  { value: 'onedrive', label: 'Excel' },
  { value: 'image_repository', label: 'Image Repository' },
  { value: 'pdf_repository', label: 'PDF Repository' },
  { value: 'notes_repository', label: 'Notes Repository' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const Workflows: React.FC = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [selectedWidgetType, setSelectedWidgetType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDescId, setExpandedDescId] = useState<string | null>(null);
  const throttledSearch = useThrottle(searchQuery);

  // Sort state
  type WfSortField = 'name' | 'status' | 'steps' | 'tags' | 'updated';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<WfSortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: WfSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'updated' ? 'desc' : 'asc');
    }
  };

  useEffect(() => {
    const loadDashboards = async () => {
      try {
        const response = await dashboardService.getDashboards(1, 100);
        setDashboards(response.data);
      } catch (err) {
        console.error('Failed to load dashboards:', err);
      }
    };
    const loadTags = async () => {
      try {
        const data = await tagService.list();
        setTags(data);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadDashboards();
    loadTags();
  }, []);

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const filters: WorkflowFilters = {};
      if (throttledSearch) filters.search = throttledSearch;
      if (selectedDashboard) filters.dashboardId = selectedDashboard;
      if (selectedWidgetType) filters.widgetType = selectedWidgetType;
      if (selectedStatus) filters.status = selectedStatus as 'draft' | 'published' | 'archived';
      if (selectedTagId) filters.tagId = selectedTagId;

      const response = await workflowService.getWorkflows(page, 12, filters);
      setWorkflows(response.data);
      setTotalPages(Math.ceil(response.total / response.pageSize));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load workflows'));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    throttledSearch,
    selectedDashboard,
    selectedWidgetType,
    selectedStatus,
    selectedTagId,
  ]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    setPage(1);
  }, [throttledSearch, selectedDashboard, selectedWidgetType, selectedStatus, selectedTagId]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDashboard('');
    setSelectedWidgetType('');
    setSelectedStatus('');
    setSelectedTagId(undefined);
  };

  const hasActiveFilters = !!(
    searchQuery || selectedDashboard || selectedWidgetType || selectedStatus || selectedTagId
  );

  const handleCreate = () => navigate('/workflow-builder');
  const handleEdit = (id: string) => navigate(`/workflow-builder/${id}`);
  const handleImportComplete = () => {
    setIsImportModalOpen(false);
    loadWorkflows();
  };

  const getNodeCount = (workflow: Workflow) => {
    const nodes = workflow.flowData?.nodes || [];
    return nodes.filter((n: any) => n.id !== 'start' && n.type !== 'output').length;
  };

  const formatRelativeDate = (dateString: string) =>
    formatRelativeDateUtil(dateString, { localeFormat: { month: 'short', day: 'numeric' } });

  const filterCount =
    [selectedDashboard, selectedWidgetType, selectedStatus, selectedTagId].filter(Boolean)
      .length + (searchQuery ? 1 : 0);

  // Sort workflows
  const STATUS_ORDER: Record<string, number> = { draft: 0, published: 1, archived: 2 };
  const sortedWorkflows = useMemo(() => {
    const sorted = [...workflows];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'status':
          cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
          break;
        case 'steps':
          cmp = getNodeCount(a) - getNodeCount(b);
          break;
        case 'tags':
          cmp = (a.tags?.length ?? 0) - (b.tags?.length ?? 0);
          break;
        case 'updated':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [workflows, sortField, sortDir]);

  return (
    <>
      <LibraryListPage
        title="Workflow Library"
        description="Build multi-step workflows with dashboards for each step"
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search workflows..."
        tags={tags}
        selectedTagId={selectedTagId}
        onTagChange={setSelectedTagId}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filterCount={filterCount}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        filterPanel={
          <>
            <Select
              value={selectedDashboard}
              onChange={e => setSelectedDashboard(e.target.value)}
              options={dashboards.map(d => ({ value: d.id, label: d.name }))}
              placeholder="All dashboards"
              label="Uses Dashboard"
              className="flex-1 min-w-[200px] max-w-xs"
            />
            <Select
              value={selectedWidgetType}
              onChange={e => setSelectedWidgetType(e.target.value)}
              options={WIDGET_TYPE_OPTIONS}
              placeholder="All widget types"
              label="Contains Widget"
              className="flex-1 min-w-[200px] max-w-xs"
            />
            <Select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              options={STATUS_OPTIONS}
              placeholder="All statuses"
              label="Status"
              className="flex-1 min-w-[150px] max-w-[200px]"
            />
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setIsImportModalOpen(true)}>
              Import
            </Button>
            {dashboards.length > 0 && (
              <Button variant="primary" size="sm" onClick={handleCreate}>
                Create Workflow
              </Button>
            )}
          </>
        }
        loading={loading}
        error={error}
        onDismissError={() => setError(null)}
        isEmpty={workflows.length === 0}
        emptyTitle={hasActiveFilters ? 'No matching workflows' : 'No workflows yet'}
        emptyDescription={
          hasActiveFilters
            ? "Try adjusting your search or filters to find what you're looking for"
            : dashboards.length === 0
              ? 'You need to create a dashboard first before you can build workflows'
              : 'Get started by creating a workflow to orchestrate multi-step processes'
        }
        emptyAction={
          hasActiveFilters
            ? { label: 'Clear filters', onClick: clearFilters }
            : dashboards.length === 0
              ? { label: 'Create Dashboard', onClick: () => navigate('/dashboards') }
              : { label: 'Create Workflow', onClick: handleCreate }
        }
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      >
        <Card variant="default">
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 border-y border-neutral-100">
                  <SortHeader label="Workflow" active={sortField === 'name'} dir={sortDir} onSort={() => toggleSort('name')} />
                  <SortHeader label="Status" active={sortField === 'status'} dir={sortDir} onSort={() => toggleSort('status')} />
                  <SortHeader label="Steps" active={sortField === 'steps'} dir={sortDir} onSort={() => toggleSort('steps')} />
                  <SortHeader label="Tags" active={sortField === 'tags'} dir={sortDir} onSort={() => toggleSort('tags')} className="hidden md:table-cell" />
                  <SortHeader label="Updated" active={sortField === 'updated'} dir={sortDir} onSort={() => toggleSort('updated')} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden lg:table-cell">Description</th>
                  <th className="px-6 py-3 hidden lg:table-cell w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sortedWorkflows.map(workflow => {
                  const stepCount = getNodeCount(workflow);
                  return (
                    <React.Fragment key={workflow.id}>
                    <tr
                      className="hover:bg-neutral-50 transition-colors cursor-pointer group"
                      onClick={() => handleEdit(workflow.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-neutral-900 group-hover:text-secondary-700 transition-colors">
                          {workflow.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={STATUS_VARIANTS[workflow.status] || 'default'} size="sm">
                          {workflow.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                          <StepsIcon />
                          {stepCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        {workflow.tags && workflow.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {workflow.tags.slice(0, 3).map(tag => (
                              <TagBadge key={tag.id} tag={tag} />
                            ))}
                            {workflow.tags.length > 3 && (
                              <Badge size="sm" variant="default">+{workflow.tags.length - 3}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs text-neutral-400">{formatRelativeDate(workflow.updatedAt)}</span>
                      </td>
                      <DescriptionCells
                        text={workflow.description}
                        expanded={expandedDescId === workflow.id}
                        onToggle={(e) => { e.stopPropagation(); setExpandedDescId(expandedDescId === workflow.id ? null : workflow.id); }}
                      />
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </LibraryListPage>

      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
        allowedTypes={['workflow', 'bundle']}
      />
    </>
  );
};

export default Workflows;
